import Dexie, { type Table } from 'dexie'
import { isSensitive } from '../utils/sensitive'

export interface CachedRecordRow {
  handle: string
  collection: string
  rkey: string
  cid: string | null
  uri: string
  value: Record<string, unknown>
  fetched_at: number
}

export interface CachedPostRow {
  uri: string
  cid: string | null
  did: string
  rkey: string
  value: Record<string, unknown>
  fetched_at: number
}

export interface CachedProfileRow {
  did: string
  handle: string
  uri: string
  profile: Record<string, unknown> | null
  fetched_at: number
}

export type GalleryVerb = 'liked' | 'reposted' | 'posted'

export interface MediaItemRow {
  id: string
  ownerHandle: string
  did: string
  rkey: string
  cid: string
  alt?: string
  likedAt: string
  isVideo: boolean
  /** Defaults to 'liked' for rows written before v3 of the schema. */
  verb?: GalleryVerb
  /** Bluesky moderation label vals attached to the source post (porn, sexual,
   *  nudity, sexual-figurative, graphic-media, etc). Undefined for rows
   *  written before v4. */
  labels?: string[]
  /** Whether the source post is a reply. Tracked from v4 onward; undefined
   *  on older rows. */
  isReply?: boolean
  fetched_at: number
}

export interface LikeHydrationRow {
  id: string
  ownerHandle: string
  /** Originally always a like rkey; now generic — can be a like or repost rkey. */
  likeRkey: string
  /** Defaults to 'liked' for rows written before v3 of the schema. */
  verb?: GalleryVerb
  status: 'has-media' | 'no-media' | 'error'
  fetched_at: number
}

class BskyDiscDB extends Dexie {
  records!: Table<CachedRecordRow, [string, string, string]>
  posts!: Table<CachedPostRow, string>
  profiles!: Table<CachedProfileRow, string>
  media_items!: Table<MediaItemRow, string>
  like_hydration!: Table<LikeHydrationRow, string>

  constructor() {
    super('bskydiscombobulate')
    this.version(1).stores({
      records: '[handle+collection+rkey], [handle+collection], handle, collection, fetched_at',
      posts: 'uri, did, fetched_at',
      profiles: 'did, &handle, fetched_at',
    })
    this.version(2).stores({
      records: '[handle+collection+rkey], [handle+collection], handle, collection, fetched_at',
      posts: 'uri, did, fetched_at',
      profiles: 'did, &handle, fetched_at',
      media_items: 'id, ownerHandle, [ownerHandle+likedAt], did, fetched_at',
      like_hydration: 'id, ownerHandle, likeRkey, fetched_at',
    })
    // v3 — repost media support: id namespace now includes verb so likes
    // and reposts coexist without colliding. Adds verb to indexes.
    this.version(3).stores({
      records: '[handle+collection+rkey], [handle+collection], handle, collection, fetched_at',
      posts: 'uri, did, fetched_at',
      profiles: 'did, &handle, fetched_at',
      media_items: 'id, ownerHandle, [ownerHandle+likedAt], [ownerHandle+verb], did, fetched_at',
      like_hydration: 'id, ownerHandle, [ownerHandle+verb], likeRkey, fetched_at',
    })
  }
}

export const db = new BskyDiscDB()

const RECORD_RANGE = (handle: string, collection: string) =>
  ({ lower: [handle, collection, ''], upper: [handle, collection, '￿'] }) as const

export async function listCached(
  handle: string,
  collection: string,
  offset = 0,
  limit = 1000,
): Promise<CachedRecordRow[]> {
  const range = RECORD_RANGE(handle, collection)
  return await db.records
    .where('[handle+collection+rkey]')
    .between(range.lower, range.upper)
    .reverse()
    .offset(offset)
    .limit(limit)
    .toArray()
}

export async function newestRkey(handle: string, collection: string): Promise<string | null> {
  const range = RECORD_RANGE(handle, collection)
  const r = await db.records
    .where('[handle+collection+rkey]')
    .between(range.lower, range.upper)
    .last()
  return r?.rkey ?? null
}

export async function countCached(handle: string, collection: string): Promise<number> {
  return await db.records.where('[handle+collection]').equals([handle, collection]).count()
}

export async function clearCache(handle: string, collection: string): Promise<void> {
  await db.records.where('[handle+collection]').equals([handle, collection]).delete()
}

export async function clearAllForHandle(handle: string): Promise<void> {
  await Promise.all([
    db.records.where('handle').equals(handle).delete(),
    db.media_items.where('ownerHandle').equals(handle).delete(),
    db.like_hydration.where('ownerHandle').equals(handle).delete(),
  ])
}

export async function upsertRecords(rows: CachedRecordRow[]): Promise<void> {
  if (rows.length === 0) return
  await db.records.bulkPut(rows)
}

export async function getCachedPost(uri: string): Promise<CachedPostRow | undefined> {
  return await db.posts.get(uri)
}

export async function upsertPost(row: CachedPostRow): Promise<void> {
  await db.posts.put(row)
}

export async function getCachedProfileByDid(did: string): Promise<CachedProfileRow | undefined> {
  return await db.profiles.get(did)
}

export async function getCachedProfileByHandle(
  handle: string,
): Promise<CachedProfileRow | undefined> {
  return await db.profiles.where('handle').equals(handle).first()
}

export async function upsertProfile(row: CachedProfileRow): Promise<void> {
  await db.profiles.put(row)
}

export async function totalRecordCount(): Promise<number> {
  return await db.records.count()
}

export async function distinctHandles(): Promise<string[]> {
  return (await db.records.orderBy('handle').uniqueKeys()) as string[]
}

const MEDIA_RANGE = (handle: string) =>
  ({ lower: [handle, ''], upper: [handle, '￿'] }) as const

export async function listMediaItems(
  ownerHandle: string,
  offset = 0,
  limit = 500,
): Promise<MediaItemRow[]> {
  const range = MEDIA_RANGE(ownerHandle)
  return await db.media_items
    .where('[ownerHandle+likedAt]')
    .between(range.lower, range.upper)
    .reverse()
    .offset(offset)
    .limit(limit)
    .toArray()
}

/**
 * Cursor-based variant — much faster than offset() when paging deep. Pass the
 * smallest likedAt you've already loaded; returns the next `limit` items
 * older than that. The compound index lookup is O(log n + limit) instead of
 * the O(offset) scan that listMediaItems(offset=N) does at high N.
 */
export async function listMediaItemsAfter(
  ownerHandle: string,
  afterLikedAt: string | null,
  limit = 500,
): Promise<MediaItemRow[]> {
  const lower: [string, string] = [ownerHandle, '']
  const upper: [string, string] = afterLikedAt
    ? [ownerHandle, afterLikedAt]
    : [ownerHandle, '￿']
  return await db.media_items
    .where('[ownerHandle+likedAt]')
    .between(lower, upper, true, false)
    .reverse()
    .limit(limit)
    .toArray()
}

export async function countMediaItems(ownerHandle: string): Promise<number> {
  return await db.media_items.where('ownerHandle').equals(ownerHandle).count()
}

export interface MediaAggregates {
  total: number
  sensitive: number
  posts: number
  replies: number
  reposts: number
  likes: number
}

/**
 * One-pass scan over the entire media_items table for a handle. Dedup-aware
 * (matches Gallery's did|rkey|cid dedup); returns true totals so the gallery
 * header can show accurate sensitive + per-source counts even when only a
 * scroll chunk is hydrated into React state.
 */
export async function getMediaAggregates(ownerHandle: string): Promise<MediaAggregates> {
  type MinRow = {
    likedAt: string
    verb?: GalleryVerb
    isReply?: boolean
    sensitive: boolean
  }
  const seen = new Map<string, MinRow>()
  await db.media_items.where('ownerHandle').equals(ownerHandle).each((it) => {
    const key = `${it.did}|${it.rkey}|${it.cid}`
    const sensitive = isSensitive(it.labels)
    const prev = seen.get(key)
    if (!prev || it.likedAt > prev.likedAt) {
      seen.set(key, { likedAt: it.likedAt, verb: it.verb, isReply: it.isReply, sensitive })
    }
  })
  let total = 0
  let sensitive = 0
  let posts = 0
  let replies = 0
  let reposts = 0
  let likes = 0
  for (const it of seen.values()) {
    total += 1
    if (it.sensitive) sensitive += 1
    if (it.verb === 'posted') {
      if (it.isReply) replies += 1
      else posts += 1
    } else if (it.verb === 'reposted') reposts += 1
    else if (it.verb === 'liked') likes += 1
  }
  return { total, sensitive, posts, replies, reposts, likes }
}

export async function bulkPutMediaItems(rows: MediaItemRow[]): Promise<void> {
  if (rows.length === 0) return
  await db.media_items.bulkPut(rows)
}

export async function bulkPutLikeHydration(rows: LikeHydrationRow[]): Promise<void> {
  if (rows.length === 0) return
  await db.like_hydration.bulkPut(rows)
}

export async function getHydratedLikeRkeys(ownerHandle: string): Promise<Set<string>> {
  const rows = await db.like_hydration.where('ownerHandle').equals(ownerHandle).toArray()
  const s = new Set<string>()
  // legacy: returns ALL hydrated rkeys regardless of verb
  for (const r of rows) s.add(r.likeRkey)
  return s
}

/** Returns the set of subject rkeys already processed for (handle, verb). */
export async function getHydratedRkeys(
  ownerHandle: string,
  verb: GalleryVerb,
): Promise<Set<string>> {
  const rows = await db.like_hydration.where('ownerHandle').equals(ownerHandle).toArray()
  const s = new Set<string>()
  for (const r of rows) {
    // pre-v3 rows have no verb — treat them as 'liked'
    const v: GalleryVerb = r.verb ?? 'liked'
    if (v === verb) s.add(r.likeRkey)
  }
  return s
}

export async function countHydration(ownerHandle: string): Promise<number> {
  return await db.like_hydration.where('ownerHandle').equals(ownerHandle).count()
}

export async function clearMediaForHandle(ownerHandle: string): Promise<void> {
  await Promise.all([
    db.media_items.where('ownerHandle').equals(ownerHandle).delete(),
    db.like_hydration.where('ownerHandle').equals(ownerHandle).delete(),
  ])
}
